import React, { useEffect, useState } from 'react';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { OnePageView } from './OnePageView';
import { LinkCheckResult } from './LinkCheck';
import Linkify from './Linkify';
import { cn, rejectReasonLabel } from './model/derive';
import { deliverableLinkRows, linkRowText } from './model/handoffSlots';
import { markedText, reviewRows, slotLabelsOf, versionsOf } from './model/relay';
import { extOf, formatSize } from './fileUtils';
import { StatusChip } from './primitives';

/** 태스크 상세 Submissions — 버전 한 줄 기록(최신 위) · 받는 사람 첫 검토 안내 */

const openKey = (viewerId, taskId) => `2days-versions:${viewerId ?? 'me'}:${taskId}`;
const guideKey = (viewerId) => `2days-review-guide:${viewerId ?? 'me'}`;

const readJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) ?? '{}') ?? {};
  } catch {
    return {};
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    return;
  }
};

const CHIP = {
  review: { label: 'In review', tone: 'green' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'failed' },
  returned: { label: 'Returned', tone: 'warning' },
};

const statusOf = (v, latest, task) =>
  latest && task.state === 'submitted'
    ? 'review'
    : v.review?.returned
      ? 'returned'
      : v.review?.result === 'rejected'
        ? 'rejected'
        : v.review?.result === 'approved'
          ? 'approved'
          : null;

const reasonOf = (r) => (r?.reason ? (rejectReasonLabel(r.reason) ?? r.reason) : r?.description ?? '');

export const FileChip = ({ file }) => {
  const size = typeof file.size === 'number' ? formatSize(file.size) : file.size;
  const body = (
    <>
      <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-green-deep03/[0.06] text-[8px] font-medium uppercase text-green-deep04'>
        {extOf(file.name)}
      </span>
      <span className='min-w-0 truncate'>{file.name}</span>
      {size && <span className='shrink-0 text-text-secondary'>{size}</span>}
    </>
  );
  const cls =
    'flex h-8 max-w-full items-center gap-2 self-start rounded-lg border border-green-deep03/[0.12] bg-grayscale-gray00 pl-1 pr-2.5 text-[13px] text-text-primary';
  return file.url ? (
    <a
      href={file.url}
      download={file.name}
      target='_blank'
      rel='noreferrer'
      title={`Download ${file.name}`}
      className={cn(cls, 'hover:bg-green-deep03/[0.03]')}
    >
      {body}
    </a>
  ) : (
    <span title='Sample file — not downloadable in the demo' className={cn(cls, 'cursor-not-allowed')}>
      {body}
    </span>
  );
};

const Mark = ({ result }) =>
  result === 'pass' ? (
    <span className='shrink-0 font-medium text-functional-success04'>✓ Pass</span>
  ) : result === 'fail' ? (
    <span className='shrink-0 font-medium text-functional-failed04'>✗ Fail</span>
  ) : (
    <span className='shrink-0 text-text-tertiary'>—</span>
  );

const ReviewRows = ({ title, rows }) => (
  <>
    <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>{title}</span>
    {rows.map((r) => (
      <span key={r.label} className='flex justify-between gap-3 text-[13px] leading-[150%]'>
        <span className='min-w-0 text-text-primary'>{r.label}</span>
        <Mark result={r.result} />
      </span>
    ))}
  </>
);

export const VersionBody = ({ deliverable, rows }) => (
  <>
    {deliverableLinkRows(deliverable).map((r) => (
      <a
        key={r.url}
        href={r.url}
        target='_blank'
        rel='noreferrer'
        className='w-fit break-all text-[14px] leading-[140%] text-green-deep03'
      >
        {linkRowText(r)}
      </a>
    ))}
    {(deliverable.files ?? []).map((f, i) => (
      <FileChip key={`${f.name}-${i}`} file={f} />
    ))}
    {deliverable.summary && (
      <span className='text-[14px] leading-[150%] text-text-tertiary'>
        <Linkify text={deliverable.summary} />
      </span>
    )}
    {deliverable.checks && (
      <div className='flex flex-col gap-1.5 rounded-xl bg-green-deep03/[0.04] px-3 py-2.5'>
        <span className='text-[12px] font-medium text-text-secondary'>Link check at submit</span>
        <LinkCheckResult result={deliverable.checks} />
      </div>
    )}
    {deliverable.onePage?.length > 0 && <OnePageView items={deliverable.onePage} />}
    {rows && (rows.slots.length > 0 || rows.items.length > 0) && (
      <div className='flex flex-col gap-1 border-t border-dashed border-green-deep03/[0.12] pt-2'>
        {rows.slots.length > 0 && <ReviewRows title='Review · Link slots' rows={rows.slots} />}
        {rows.items.length > 0 && <ReviewRows title='Review · Task List' rows={rows.items} />}
      </div>
    )}
  </>
);

const VersionLog = ({ task, items, viewerId, name, fmt }) => {
  const versions = versionsOf(task);
  const key = openKey(viewerId, task.id);
  const [open, setOpen] = useState(() => readJson(key));
  useEffect(() => setOpen(readJson(key)), [key]);
  if (!versions.length) return null;
  const isOpen = (v, i) => open[v.v] ?? i === 0;
  const toggle = (v, i) => {
    const next = { ...open, [v.v]: !isOpen(v, i) };
    setOpen(next);
    write(key, JSON.stringify(next));
  };
  return (
    <div className='flex flex-col gap-2'>
      <span className='text-[13px] leading-[140%] text-text-secondary'>
        Submissions ({versions.length})
      </span>
      <ol className='flex flex-col'>
        {versions.map((v, i) => {
          const latest = i === 0;
          const st = statusOf(v, latest, task);
          const d = v.deliverable ?? {};
          const expanded = isOpen(v, i);
          const result =
            st === 'rejected'
              ? [
                  (v.review.failedItems ?? []).length
                    ? `✗ ${markedText(v.review.failedItems, slotLabelsOf(d))}`
                    : '',
                  reasonOf(v.review),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : st === 'returned'
                ? ['Returned', reasonOf(v.review)].filter(Boolean).join(' · ')
                : '';
          return (
            <li key={v.v} className='grid grid-cols-[16px_minmax(0,1fr)] gap-x-3'>
              <span className='relative flex justify-center'>
                {i < versions.length - 1 && (
                  <span
                    aria-hidden
                    className='absolute bottom-0 top-[26px] w-[1.5px] bg-green-deep03/[0.16]'
                  />
                )}
                <span
                  aria-hidden
                  className={cn(
                    'relative mt-4 h-2.5 w-2.5 rounded-full',
                    latest
                      ? 'bg-green-deep02 shadow-[0_0_0_3px_#e6f5ec]'
                      : 'bg-grayscale-gray00 shadow-[inset_0_0_0_2px_theme(colors.grayscale.gray04)]',
                  )}
                />
              </span>
              <div className='mb-3 min-w-0 overflow-hidden rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4'>
                <button
                  type='button'
                  aria-expanded={expanded}
                  onClick={() => toggle(v, i)}
                  className='flex min-h-[44px] w-full flex-wrap items-center gap-2 py-2.5 pl-3 pr-4 text-left hover:bg-green-deep03/[0.03]'
                >
                  <ChevronIcon
                    aria-hidden
                    className={cn(
                      'h-4 w-4 shrink-0 text-icon-secondary transition-transform',
                      !expanded && '-rotate-90',
                    )}
                  />
                  <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                    v{v.v}
                  </span>
                  {st && <StatusChip chip={CHIP[st]} className='rounded-full px-[9px]' />}
                  <span className='text-[13px] leading-[140%] text-text-secondary'>
                    {name(d.submittedBy ?? task.assigneeId)} · {fmt(d.submittedAt)}
                  </span>
                </button>
                {result && (
                  <p
                    className={cn(
                      '-mt-1 px-4 pb-2.5 pl-9 text-[13px] leading-[150%]',
                      st === 'rejected' ? 'text-functional-failed04' : 'text-functional-warning04',
                    )}
                  >
                    {result}
                  </p>
                )}
                {expanded && (
                  <div className='flex flex-col gap-2 border-t border-green-deep03/[0.08] py-3 pl-9 pr-4'>
                    <VersionBody deliverable={d} rows={reviewRows(v, items)} />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const STEPS = [
  [
    'Check each link slot.',
    'Open every link below. Mark ✓ if it opens with your access and has what the slot asks for, ✗ if not.',
  ],
  ['Mark the Task List.', 'Pick ✓ or ✗ for every item.'],
  [
    'Decide.',
    'Approve when every link slot and Task List item is ✓. Reject needs at least one ✗ (slot or item), a reason and a description.',
  ],
];

export const useReviewGuide = (viewerId) => {
  const key = guideKey(viewerId);
  const read = () => {
    try {
      return !!window.localStorage.getItem(key);
    } catch {
      return false;
    }
  };
  const [seen, setSeen] = useState(read);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSeen(read()), [key]);
  return {
    seen,
    dismiss: () => {
      setSeen(true);
      write(key, '1');
    },
  };
};

export const ReviewGuide = ({ onDismiss }) => (
  <div
    role='dialog'
    aria-label='How to review'
    className='relative flex flex-col gap-3 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 px-5 py-4 shadow-popup-01'
  >
    <span
      aria-hidden
      className='absolute -bottom-[7px] left-7 h-3 w-3 rotate-45 border-b border-r border-green-deep03/[0.08] bg-grayscale-gray00'
    />
    <div className='flex flex-col gap-0.5'>
      <span className='text-[15px] font-medium leading-[140%] text-text-primary'>
        How to review this handoff
      </span>
      <span className='text-[13px] leading-[150%] text-text-secondary'>You’ll see this once.</span>
    </div>
    <ol className='flex flex-col gap-2.5'>
      {STEPS.map(([lead, rest], i) => (
        <li key={lead} className='flex items-start gap-2.5'>
          <span className='flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[12px] font-medium text-green-deep04'>
            {i + 1}
          </span>
          <span className='text-[14px] leading-[150%] text-text-primary'>
            <span className='font-medium'>{lead}</span> {rest}
          </span>
        </li>
      ))}
    </ol>
    <div className='flex justify-end'>
      <button
        type='button'
        onClick={onDismiss}
        className='h-9 rounded-full bg-green-deep02 px-4 text-[14px] font-medium text-grayscale-gray00 hover:bg-green-deep04'
      >
        Got it
      </button>
    </div>
  </div>
);

export default VersionLog;
